<template>
  <div
    :style="{ 'z-index': getPopupZindex }"
    :class="{'customer-overlay': true, 'is-visible': isVisible }"
    @click="closePopup">
    <div
      :class="{ 'customer': true, 'is-loading': loadingData }"
      @click.stop.prevent="popupClicked">
      <a
        href="#"
        class="customer-popup-close"
        @click.prevent="closePopup">
        &times;
      </a>

      <div
        v-if="loadingData"
        class="customer-empty-state">
        {{ $t('common.ui.loadingData') }}
      </div>

      <div
        v-if="customerID > 0 && customerLoaded && !loadingData"
        class="customer-wrapper">
        <h2 class="customer-name">
          <span v-if="this.customer.name === ''">
            {{ $t('customerPopup.noData') }}
          </span>

          <span v-if="this.customer.name !== ''">
            {{ customer.name }}
          </span>

          <btn
            v-if="!limitedMode && userCanAddReservation"
            type="success with-icon"
            iconName="plus"
            iconSize="xxxs"
            iconColor="color-light"
            slot="bottom-ui"
            @click.native="addReservation()">
            {{ $t('common.ui.addReservation') }}
          </btn>

          <btn
            v-if="!limitedMode"
            :type="removeInProgress ? 'loader icon' : 'icon'"
            :title="$t('customerPopup.removeClient')"
            @click.native="askForRemove"
            :disabled="removeInProgress"
            :iconName="!removeInProgress ? 'cross-circle' : 'loader'"
            iconColor="color-danger" />
        </h2>

        <div class="customer-notes">
          <h3>
            {{ $t('customerPopup.notesAboutClient') }}
          </h3>

          <p v-if="!notesLoaded">{{ $t('common.ui.loading') }}...</p>
          <p v-if="notesLoaded && notes.length === 0">{{ $t('customerPopup.noNotes') }}</p>

          <customer-note
            v-for="(item, index) of notes"
            :key="'note-' + index"
            :noteData="item"
            :customerID="customerID" />

          <a
            href="#"
            class="customer-notes-add"
            @click.prevent="addNote">
            <icon
              name="plus-circle"
              size="xs"
              primaryColor="color-success" />
            {{ $t('customerPopup.addNewNote') }}
          </a>
        </div>

        <div class="customer-contact-data">
          <h3>
            {{ $t('customerPopup.contactData') }}
            <a
              v-if="!limitedMode && !userIsWorker"
              href="#"
              class="customer-contact-data-edit"
              @click.prevent="editData">
              <icon
                name="edit"
                size="xxs"
                primaryColor="color-notice" />
              {{ $t('common.ui.edit') }}
            </a>
          </h3>

          <span
            v-if="customer.phone"
            class="customer-phone">
            <icon
              name="phone"
              primaryColor="color-text-light"
              size="xs" />
            <span>{{ customer.phone }}</span>
          </span>

          <span
            v-if="customer.email"
            class="customer-email">
            <icon
              name="mailbox"
              primaryColor="color-text-light"
              size="xs" />
            <span>{{ customer.email }}</span>
          </span>

          <h3>{{ $t('customerPopup.customerAgreements') }}</h3>

          <div class="customer-agree">
            <span class="customer-agree-label">
              {{ $t('customerPopup.acceptedTosAndPrivacyPolicy') }}
            </span>
            <span :class="{ 'customer-agree-response': true, 'customer-agree-response-yes': customer.rodo.agree_tp, 'customer-agree-response-no': !customer.rodo.agree_tp }">
              <template v-if="customer.rodo.agree_tp">{{ $t('customerPopup.yes') }}</template>
              <template v-if="!customer.rodo.agree_tp">{{ $t('customerPopup.no') }}</template>
            </span>
            <span class="customer-agree-last-modification">
              ({{ $t('customerPopup.lastChanged') }} {{ customer.rodo.agree_tp_last_date }})
            </span>
          </div>

          <div class="customer-agree">
            <span class="customer-agree-label">
              {{ $t('customerPopup.agreementForEmail') }}
            </span>
            <span :class="{ 'customer-agree-response': true, 'customer-agree-response-yes': customer.rodo.agree_newsletter, 'customer-agree-response-no': !customer.rodo.agree_newsletter }">
              <template v-if="customer.rodo.agree_newsletter">{{ $t('customerPopup.yes') }}</template>
              <template v-if="!customer.rodo.agree_newsletter">{{ $t('customerPopup.no') }}</template>
            </span>
            <span class="customer-agree-last-modification">
              ({{ $t('customerPopup.lastChanged') }} {{ customer.rodo.agree_newsletter_last_date }})
            </span>
          </div>

          <div class="customer-agree">
            <span class="customer-agree-label">
              {{ $t('customerPopup.agreementForPhone') }}
            </span>
            <span :class="{ 'customer-agree-response': true, 'customer-agree-response-yes': customer.rodo.agree_telemarketing, 'customer-agree-response-no': !customer.rodo.agree_telemarketing }">
              <template v-if="customer.rodo.agree_telemarketing">{{ $t('customerPopup.yes') }}</template>
              <template v-if="!customer.rodo.agree_telemarketing">{{ $t('customerPopup.no') }}</template>
            </span>
            <span class="customer-agree-last-modification">
              ({{ $t('customerPopup.lastChanged') }} {{ customer.rodo.agree_telemarketing_last_date }})
            </span>
          </div>
        </div>

        <div class="customer-stats">
          <h3>
            {{ $t('customerPopup.customerStats') }}
          </h3>

          <div class="customer-stats-wrapper">
            <stats-box
              v-if="paymentsEnabled"
              color="primary"
              :title="$t('customerPopup.reservations')"
              iconName="calendar-big"
              :value="reservationsCount"
              :footerTitle="$t('customerPopup.totalIncome')"
              :minimalMode="false"
              :footerValue="customerTotalIncome" />

            <stats-box
              v-if="!paymentsEnabled"
              color="primary"
              :title="$t('customerPopup.reservations')"
              iconName="calendar-big"
              :value="reservationsCount"
              :footerTitle="$t('customerPopup.reservationsInTheLastMonth')"
              :minimalMode="false"
              :footerValue="customer.stats.reservations_last_month" />

            <stats-box
              color="success"
              :title="$t('customerPopup.accepted')"
              iconName="check-circle-big"
              :value="acceptedReservationsCount"
              :footerTitle="$t('customerPopup.totalPercent')"
              :minimalMode="false"
              :footerValue="acceptedReservationsTotalPercent + '%'" />
          </div>
        </div>

        <div class="customer-reservations">
          <h3>
            {{ $t('customerPopup.reservationsHistory') }}

            <a
              v-if="reservations.length"
              href="#"
              @click.prevent="toggleFilters()">
              <template v-if="!showFilters">{{ $t('customerPopup.filterReservations') }}</template>
              <template v-if="showFilters">{{ $t('common.ui.clearFilters') }}</template>
            </a>
          </h3>

          <div
            v-if="showFilters"
            class="customer-reservations-filters">
            <date-picker
              slot="field"
              :language="$t('common.ui.datepickerLanguage')"
              :full-month-name="true"
              v-model="reservationFilters.date"
              :format="$t('common.ui.datepickerFormat')"
              :monday-first="true"
              :placeholder="$t('customerPopup.goToDate')"
              :clear-button="true"></date-picker>

            <select
              v-if="reservationWorkers.length"
              v-model="reservationFilters.worker"
              slot="field">
              <option key="worker-rw-00" value="">{{ $t('common.ui.allWorkers') }}</option>
              <option
                v-for="(workerData, index) of reservationWorkers"
                :key="'worker-rw-' + index"
                :value="workerData.id">
                {{ workerData.name }}
              </option>
            </select>

            <select
              v-if="reservationServices.length"
              v-model="reservationFilters.service"
              slot="field">
              <option key="service-00" value="">{{ $t('common.ui.allServices') }}</option>
              <option
                v-for="(service, index) of reservationServices"
                :value="service.id"
                :key="'service-' + index">
                {{ service.name }}
              </option>
            </select>
          </div>

          <p
            v-if="!reservations.length"
            class="customer-reservations-empty-state">
            {{ $t('common.ui.noReservations') }}
          </p>

          <p
            v-if="reservations.length && !visibleReservations.length"
            class="customer-reservations-empty-state">
            {{ $t('common.ui.noReservationsForGivenFilters') }}
          </p>

          <customer-reservation
            v-for="(item, index) of reservationsPagination"
            :key="'customer-reservation-' + index"
            :reservationData="item"
            :itemStyle="index % 2 === 0 ? 'odd' : 'even'">
          </customer-reservation>

          <pagination
            v-if="visibleReservations.length"
            ref="pagination"
            :max="paginationMax"
            :meta="false"
            :current="paginationCurrent"
            :onChange="reloadReservations"
            :activeFilters="hasActiveFilters" />
        </div>
      </div>

      <btn
        type="danger full-width"
        @click.native="closePopup">
        {{ $t('common.ui.close') }}
      </btn>
    </div>
  </div>
</template>

<script>
import debounce from 'lodash.debounce';
import CustomerNote from './../ui/CustomerNote';
import CustomerReservation from './../ui/CustomerReservation';
import DatePicker from 'vuejs-datepicker';
import ErrorUtils from './../helpers/ErrorUtils.js';
import Pagination from './../common/Pagination';
import PopupZindex from './../mixins/PopupZindex';
import StatsBox from './StatsBox';
import UserTypes from './../mixins/UserTypes';

export default {
  name: 'customer-popup',
  mixins: [
    UserTypes,
    PopupZindex
  ],
  props: {
    'id': {
      default: 0
    }
  },
  components: {
    'customer-note': CustomerNote,
    'customer-reservation': CustomerReservation,
    'date-picker': DatePicker,
    'pagination': Pagination,
    'stats-box': StatsBox
  },
  data () {
    return {
      isVisible: false,
      customerID: 0,
      loadingData: false,
      limitedMode: false,
      customer: null,
      reservations: null,
      paginationCurrent: 1,
      customerLoaded: false,
      removeInProgress: false,
      notes: [],
      notesLoaded: false,
      showFilters: false,
      reservationFilters: {
        service: '',
        date: '',
        worker: ''
      },
      reservationWorkers: [],
      reservationServices: []
    }
  },
  watch: {
    'reservationFilters': {
      handler: function (newValue) {
        this.paginationCurrent = 1;

        if (this.$refs['pagination']) {
          this.$refs['pagination'].reset();
        }
      },
      deep: true
    }
  },
  computed: {
    paymentsEnabled () {
      return !!this.$store.state.config.company.payment;
    },
    reservationsCount () {
      return this.reservations.length;
    },
    customerTotalIncome () {
      return Number(this.customer.stats.total_income).toFixed(2) + this.$t('common.ui.incomeCurrency');
    },
    acceptedReservationsCount () {
      return this.reservations.filter(reservation => reservation.status === 3).length;
    },
    acceptedReservationsTotalPercent () {
      if (!this.reservations.length) {
        return '- - %';
      }

      return Number((this.acceptedReservationsCount / this.reservations.length) * 100.0).toFixed(2);
    },
    paginationMax () {
      return Math.ceil(this.visibleReservations.length / 10);
    },
    visibleReservations () {
      let selectedDate = false;

      if (this.reservationFilters.date) {
        selectedDate = [
          this.reservationFilters.date.getFullYear(),
          this.reservationFilters.date.getMonth() + 1,
          this.reservationFilters.date.getDate()
        ].map(n => n < 10 ? '0' + n : n).join('-');
      }

      return this.reservations.filter(item => {
        if (this.reservationFilters.service !== '') {
          if (!item.service || item.service.id !== this.reservationFilters.service) {
            return false;
          }
        }

        if (this.reservationFilters.worker !== '') {
          if (!item.worker || item.worker.id !== this.reservationFilters.worker) {
            return false;
          }
        }

        if (this.reservationFilters.date) {
          if (item.date !== selectedDate) {
            return false;
          }
        }

        return true;
      });
    },
    reservationsPagination () {
      return this.visibleReservations.filter((item, index) => {
        return index >= (this.paginationCurrent - 1) * 10 && index < this.paginationCurrent * 10;
      });
    },
    hasActiveFilters () {
      if (
        this.reservationFilters.service === '' &&
        this.reservationFilters.date === '' &&
        this.reservationFilters.worker === ''
      ) {
        return false;
      }

      return true;
    }
  },
  mounted () {
    this.$bus.$on('show-customer-popup', this.showCustomerPopup);
    this.$bus.$on('hide-customer-popup', this.closePopup);
    this.$bus.$on('customer-reload-needed', this.customerReloadNeeded);
    this.$bus.$on('customer-notes-reload-needed', this.loadNotes);
  },
  methods: {
    showCustomerPopup (customerID, limitedMode = false) {
      this.limitedMode = limitedMode;
      this.isVisible = true;
      this.customerID = customerID;
      this.loadingData = true;

      if (this.customerID > 0) {
        this.loadData();
        this.reservationFilters = {
          service: '',
          date: '',
          worker: ''
        };
        this.showFilters = false;
      }

      this.increasePopupZindex();
    },
    customerReloadNeeded () {
      if (this.customerID > 0) {
        this.loadData();
      }
    },
    loadData: debounce(function () {
      this.getData();
    }, 1000),
    getData () {
      this.loadingData = true;

      if (!this.isVisible) {
        this.loadingData = false;
        return;
      }

      this.$http.post('/panel/customers/get-customer', {
        'id': this.customerID
      }).then(response => {
        if (response.data && response.data.customer) {
          this.customer = response.data.customer;
          this.reservations = response.data.reservations;

          if (response.data.services) {
            this.reservationServices = Object.keys(response.data.services).map(id => ({
              id: response.data.services[id].id,
              name: response.data.services[id].name
            }));
          }

          if (response.data.workers) {
            this.reservationWorkers = Object.keys(response.data.workers).map(id => ({
              id: response.data.workers[id].id,
              name: response.data.workers[id].name
            }));
          }

          this.loadingData = false;
          this.customerLoaded = true;
          this.loadNotes();
        } else {
          this.$bus.$emit('alert-display', {
            title: this.$t('customerPopup.unableToLoadCustomer'),
            desc: this.$t('customerPopup.unableToLoadCustomerDesc'),
            okLabel: this.$t('common.ui.ok'),
            okClick: () => this.closePopup(),
            isDanger: true
          });
        }
      }).catch(error => {
        ErrorUtils.handleSubmitError(error, '/panel/customers/get-customer', this.$router, this.$bus, this.getData.bind(this));
        this.loadingData = false;
      });
    },
    askForRemove () {
      let showCheckbox = this.reservations && this.reservations.length > 0;

      this.$bus.$emit('confirm-display', {
        title: this.$t('customerPopup.removingCustomerTitle'),
        desc: this.$t('customerPopup.removingCustomerDesc'),
        okLabel: this.$t('customerPopup.removingCustomerBtn'),
        okClick: (withInquiries) => this.remove(withInquiries),
        checkbox: showCheckbox,
        checkboxLabel: this.$t('customersListItem.removeAllReservationsWithCustomer', { count: this.reservations.length }),
        checkboxDefaultValue: false
      });
    },
    remove (withInquiries = false) {
      this.removeInProgress = true;

      let dataToSend = {
        id: this.customerID
      };

      if (withInquiries === true) {
        dataToSend.with_inquiries = true;
      }

      this.$http.post('/panel/customers/remove-customer', dataToSend).then(response => {
        this.$bus.$emit('customers-reload-needed', false);
        this.$bus.$emit('customer-removed');

        this.$bus.$emit('notification-display', {
          message: this.$t('customerPopup.selectedClientRemoved'),
          type: 'success'
        });

        this.isVisible = false;
        this.customerLoaded = false;
        this.removeInProgress = false;
      }).catch(error => {
        ErrorUtils.handleSubmitError(error, '/panel/customers/remove-customer', this.$router, this.$bus, this.remove.bind(this, withInquiries));
        this.removeInProgress = false;
      });
    },
    editData () {
      this.$bus.$emit('show-customer-form', {
        id: this.customerID,
        name: this.customer.name,
        email: this.customer.email,
        phone: this.customer.phone
      });
    },
    loadNotes () {
      this.$http.post('/panel/customers/customer-get-notes', {
        'customer_id': this.customerID
      }).then(response => {
        this.notes = response.data.notes;
        this.notesLoaded = true;
      }).catch(error => {
        ErrorUtils.handleSubmitError(error, '/panel/customers/customer-get-ntoes', this.$router, this.$bus, this.loadNotes.bind(this));
        this.notesLoaded = true;
      });
    },
    addNote () {
      this.$bus.$emit('show-customer-note-form', {
        customerID: this.customerID
      });
    },
    reloadReservations (pageNumber) {
      this.paginationCurrent = pageNumber;
    },
    addReservation () {
      this.$bus.$emit('hide-reservation-details-popup');
      this.$router.push('/reservation/new/customer/' + this.customerID);
      this.closePopup();
    },
    closePopup () {
      this.isVisible = false;
      this.customerLoaded = false;
      this.customer = null;
      this.customerID = 0;
      this.notes = [];
      this.reservations = [];
      this.reservationServices = []
      this.reservationWorkers = [];
      this.removePopupZindex();
    },
    toggleFilters () {
      this.showFilters = !this.showFilters;

      if (!this.showFilters) {
        this.reservationFilters = {
          service: '',
          date: '',
          worker: ''
        };
      }
    }
  },
  beforeDestroy () {
    this.$bus.$off('show-customer-popup', this.showCustomerPopup);
    this.$bus.$off('hide-customer-popup', this.closePopup);
    this.$bus.$off('customer-reload-needed', this.customerReloadNeeded);
    this.$bus.$off('customer-notes-reload-needed', this.loadNotes);
  }
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.customer {
  background: $color-dark;
  border-radius: $border-radius-small;
  color: $color-text-light;
  display: flex;
  height: 90vh;
  left: 50%;
  padding: 1rem 0 0 0;
  position: absolute;
  top: 50%;
  transform: translateX(-50%) translateY(-50%);
  transition: opacity .25s ease-out;
  width: 720px;
  will-change: opacity;

  &-overlay {
    background: rgba(0, 0, 0, .25);
    height: 100%;
    left: 0;
    opacity: 0;
    pointer-events: none;
    position: fixed;
    top: 0;
    transition: opacity .25s ease-out;
    width: 100vw;
    will-change: opacity;

    &.is-visible {
      opacity: 1;
      pointer-events: auto;
    }
  }

  &-popup-close {
    background: $color-danger;
    border-radius: 50%;
    color: $color-light;
    font-size: 3.6rem;
    font-weight: bold;
    height: 3.6rem;
    line-height: 3.5rem;
    position: absolute;
    right: -1.8rem;
    text-align: center;
    top: -1.8rem;
    width: 3.6rem;
  }

  .customer-wrapper {
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    padding: 1rem 2.5rem 2rem 2.5rem;
    width: 100%;
  }

  .customer-empty-state {
    font-size: 2.4rem;
    padding: 3.2rem 0;
    text-align: center;
    width: 100%;
  }

  &-name {
    border-bottom: 1px solid rgba(255, 255, 255, .125);
    color: $color-light;
    display: flex;
    font-size: 3.2rem;
    font-weight: 600;
    margin: 0;
    padding: 1.5rem 0;

    .button {
      margin-left: auto;

      & + .button {
        margin-left: 1rem;
        margin-right: 0;
      }
    }
  }

  &-notes {
    border-bottom: 1px solid rgba(255, 255, 255, .125);
    padding: 2rem 0 4rem 0;

    & > h3 {
      font-size: 1.2rem;
      margin-bottom: 2rem;
      text-transform: uppercase;
    }

    &-add {
      color: $color-success;
      font-size: 1.3rem;
      line-height: 2.4rem;
      position: relative;
      top: .5rem;

      .icon {
        margin-right: .5rem;
        position: relative;
        top: .3rem;
      }

      &:active,
      &:focus,
      &:hover {
        opacity: .75;
      }
    }
  }

  &-contact-data {
    border-bottom: 1px solid rgba(255, 255, 255, .125);
    padding: 2rem 0 4rem 0;

    & > h3 {
      font-size: 1.2rem;
      margin-bottom: 2rem;
      text-transform: uppercase;

      &:nth-of-type(2) {
        margin-top: 2rem;
      }
    }

    &-edit {
      margin-left: 1rem;
      text-transform: none;

      svg {
        position: relative;
        top: 2px;
      }
    }

    & > span {
      & > .icon {
        margin-right: 1rem;
      }

      & > span {
        color: $color-light;
        font-size: 2.4rem;
        margin-right: 5rem;
      }
    }
  }

  &-agree {
    align-items: center;
    display: flex;
    font-size: 1.3rem;
    padding: .5rem 0;

    &:last-child {
      padding-bottom: 0;
    }

    &-label {
      width: 60%;
    }

    &-response {
      font-weight: bold;
      padding: 0 1rem;
      width: 5%;

      &-yes {
        color: $color-success;
      }

      &-no {
        color: $color-danger;
      }
    }

    &-last-modification {
      text-align: right;
      width: 35%;
    }
  }

  &-stats {
    border-bottom: 1px solid rgba(255, 255, 255, .125);
    padding: 2rem 0 4rem 0;

    & > h3 {
      font-size: 1.2rem;
      margin-bottom: 2rem;
      text-transform: uppercase;
    }

    &-wrapper {
      align-items: stretch;
      display: flex;
      height: 175px;

      .stats-box {
        margin-bottom: 0!important;
      }
    }
  }

  &-reservations {
    padding: 2rem 0 4rem 0;

    & > h3 {
      display: flex;
      font-size: 1.2rem;
      margin-bottom: 2rem;
      text-transform: uppercase;

      a {
        margin-left: auto;
      }
    }

    .pagination-wrapper {
      margin-top: 2rem;
      padding: 0;
    }

    &-filters {
      display: flex;
      justify-content: space-between;
      padding-bottom: 2rem;

      & > * {
        width: 32%;
      }
    }

    &-empty-state {
      text-align: center;
    }
  }

  & > .button {
    display: none;
  }
}

@media (max-width: 800px) {
  .customer {
    height: 100%;
    left: 0;
    top: 0;
    transform: none;
    width: 100vw;

    & > .button {
      border-radius: 0;
      bottom: 0;
      display: block;
      left: 0;
      position: absolute;
    }

    .customer-popup-close {
      display: none;
    }
  }
}

@media (max-width: 700px) {
  .customer {
    .customer-name {
      font-size: 2.4rem;
    }

    .customer-contact-data > span > span {
      font-size: 1.4rem;
      position: relative;
      top: -4px;
    }

    .customer-agree {
      flex-wrap: wrap;

      .customer-agree-label {
        padding-bottom: .5rem;
        width: 100%;
      }

      .customer-agree-response {
        padding-left: 0;
        width: 35px;
      }

      .customer-agree-last-modification {
        text-align: left;
        width: calc(100% - 35px);
      }
    }
  }
}

@media (max-width: 600px) {
  .customer {
    .customer-wrapper {
      padding: 1rem;
    }

    .customer-name {
      flex-wrap: wrap;
      margin-top: -2rem;

      & > span {
        margin-bottom: 1.5rem;
        width: 100%;
      }

      & > .button {
        margin: 0 1rem 0 0;
        width: 75%;

        & + .button {
          margin: 0;
          width: calc(25% - 1rem);
        }
      }
    }

    .customer-email,
    .customer-phone {
      display: block;
      margin-top: 1rem;
      width: 100%;
    }

    .customer-reservations-filters {
      flex-wrap: wrap;

      & > * {
        margin: 0 0 1rem 0;
        width: 100%;
      }
    }
  }
}

@media (max-width: 480px) {
  .customer {
    .customer-stats-wrapper {
      flex-wrap: wrap;
      height: auto;
      margin-bottom: -2rem;

      & > * {
        margin: 0 0 2rem 0!important;
        width: 100%;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ui/CustomerPopup.vue